import { useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { CheckCircle2, CornerDownRight, Plus } from "lucide-react";
import { markZoomCallTasksCreated } from "@/modules/lead-page/api/lead-page.v2.api";
import AddNewTask from "@/modules/tasks/components/add-task";
import { formatLabel } from "@/modules/zoom-calls/components/analysis.helpers";
import type { StructuredExtractedFields } from "@/modules/zoom-calls/types";

interface NextAction {
  /** Task title prefill, e.g. "Follow up promised". */
  label: string;
  /** ISO-ish datetime used for the due-date prefill (may be absent). */
  due: string | null;
  /** Row display text: label + formatted due time when it parses. */
  display: string;
}

const parseDate = (raw?: string | null): Date | null => {
  if (!raw || !String(raw).trim()) return null;
  // Payload datetimes come as "2026-07-14 14:31:35.098 +0000" — normalize for Date/parseISO.
  const normalized = String(raw).trim().replace(" ", "T").replace(" +", "+");
  const viaIso = parseISO(normalized);
  if (!isNaN(viaIso.getTime())) return viaIso;
  const viaNative = new Date(raw);
  return isNaN(viaNative.getTime()) ? null : viaNative;
};

const buildAction = (label: string, rawDate?: string | null): NextAction => {
  const date = parseDate(rawDate);
  return {
    label,
    due: date ? date.toISOString() : null,
    display: date ? `${label} — ${format(date, "EEE d MMM, h:mm a")}` : label,
  };
};

/** Derives the actionable next steps from the structured call-summary flags. */
const buildActions = (data: StructuredExtractedFields | null): NextAction[] => {
  if (!data) return [];
  const actions: NextAction[] = [];

  const meeting = data.meeting_scheduled;
  if (meeting?.scheduled) {
    const context = [meeting.medium, meeting.location]
      .map((v) => (v ? formatLabel(String(v).trim()) : null))
      .filter(Boolean)
      // "online" medium + "Online" location read as duplicates — keep unique values.
      .filter((v, i, arr) => arr.indexOf(v) === i)
      .join(", ");
    actions.push(
      buildAction(
        context ? `Meeting scheduled (${context})` : "Meeting scheduled",
        meeting.datetime,
      ),
    );
  }

  const followUp = data.follow_up_promised;
  if (followUp?.promised) {
    actions.push(buildAction("Follow up promised", followUp.when));
  }

  const siteVisit = data.site_visit_proposed;
  if (siteVisit?.proposed) {
    actions.push(buildAction("Site visit proposed", siteVisit.date));
  }

  return actions;
};

interface NextActionsCardProps {
  data: StructuredExtractedFields | null;
  callUid: string;
  tasksCreatedAt: string | null;
  lead: { uid: string; nanoId: string; name: string } | null;
  assignee: { uid: string; name: string } | null;
}

/**
 * "Next action" card in the call Overview: one row per actionable outcome
 * (meeting / follow-up / site visit) plus a "Create tasks from outcome" CTA.
 * Rows and the CTA open the Task-Manager create-task modal prefilled; after a
 * confirmed creation the CTA is replaced by a persisted "tasks created" state.
 */
const NextActionsCard = ({
  data,
  callUid,
  tasksCreatedAt,
  lead,
  assignee,
}: NextActionsCardProps) => {
  const theme = useTheme();
  const p = theme.nawy.colors.primitives;

  /* One create-task run works through a queue of actions: a row queues just
     itself, the CTA queues every action. The modal opens prefilled for
     queue[queueIndex] and advances after each confirmed creation. */
  const [queue, setQueue] = useState<NextAction[]>([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [justCreated, setJustCreated] = useState(false);

  const markCreated = useMutation({
    mutationFn: () => markZoomCallTasksCreated(callUid),
  });

  const actions = buildActions(data);
  const taken = Boolean(tasksCreatedAt) || justCreated;
  const takenDate = parseDate(tasksCreatedAt);

  // No actionable next steps and none were created yet → hide the whole card
  // instead of showing an empty "Next action" section.
  if (!taken && actions.length === 0) return null;

  const openFor = (list: NextAction[]) => {
    setQueue(list);
    setQueueIndex(0);
    setModalOpen(true);
  };

  const handleTaskCreated = () => {
    const next = queueIndex + 1;
    if (next < queue.length) {
      /* More outcome tasks queued — advancing the index remounts the modal
         (it's keyed on it) prefilled with the next action. */
      setQueueIndex(next);
      return;
    }
    setModalOpen(false);
    setJustCreated(true);
    markCreated.mutate();
  };

  const currentAction = queue[queueIndex] ?? null;

  return (
    <Box
      sx={{
        p: "12px",
        borderRadius: "12px",
        backgroundColor: p.white[200],
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 400,
          lineHeight: "16px",
          color: theme.nawy.colors.semantic.text.tertiary,
          whiteSpace: "nowrap",
        }}
      >
        Next action
      </Typography>

      {!taken &&
        actions.map((action, i) => (
          <Box
            key={i}
            component="button"
            onClick={() => openFor([action])}
            sx={{
              appearance: "none",
              fontFamily: "inherit",
              textAlign: "left",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              px: "12px",
              py: "8px",
              borderRadius: "8px",
              border: "none",
              backgroundColor: theme.nawy.colors.semantic.layer.primary,
              color: p.navy[800],
              fontSize: 13,
              fontWeight: 500,
              "&:hover": { color: p.blue[600] },
            }}
          >
            <Plus size={14} style={{ flexShrink: 0 }} />
            {action.display}
          </Box>
        ))}

      {!taken && actions.length > 0 && (
        <Box
          component="button"
          onClick={() => openFor(actions)}
          sx={{
            appearance: "none",
            fontFamily: "inherit",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "fit-content",
            px: "16px",
            py: "8px",
            mt: "2px",
            borderRadius: "999px",
            border: "none",
            backgroundColor: theme.nawy.colors.semantic.layer.accent,
            color: theme.nawy.colors.semantic.text.onColor,
            fontSize: 13,
            fontWeight: 600,
            "&:hover": { opacity: 0.92 },
          }}
        >
          <CornerDownRight size={14} style={{ flexShrink: 0 }} />
          Create tasks from outcome
        </Box>
      )}

      {taken && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <CheckCircle2
            size={16}
            color={theme.nawy.colors.semantic.indicator.green}
            style={{ flexShrink: 0 }}
          />
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: theme.nawy.colors.semantic.indicator.green,
            }}
          >
            {takenDate
              ? `Tasks created from call outcome — ${format(takenDate, "EEE d MMM, h:mm a")}`
              : "Tasks created from call outcome"}
          </Typography>
        </Box>
      )}

      <AddNewTask
        /* Keyed per queue position: each task gets a fresh modal instance, so
           the form and its AI-evaluation state never leak into the next one. */
        key={queueIndex}
        hideTrigger
        open={modalOpen}
        onOpenChange={setModalOpen}
        // The zoom-call side drawer pins itself at zIndex 1400 — lift the task
        // dialog above it (still below MUI tooltips at 1500).
        modalSx={{ zIndex: 1450 }}
        header={
          queue.length > 1
            ? `Add Task (${queueIndex + 1} of ${queue.length})`
            : undefined
        }
        prefilledTitle={currentAction?.label}
        prefilledDueDate={currentAction?.due ?? undefined}
        prefilledLead={lead ?? undefined}
        prefilledAssignee={assignee ?? undefined}
        disableLead={Boolean(lead)}
        disableAssignee={Boolean(assignee)}
        onTaskCreated={handleTaskCreated}
      />
    </Box>
  );
};

export default NextActionsCard;
